import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { createSubscription, 
        updateSubscription, 
        deleteSubscription, 
        updateBeneficiario, 
        fetchSubscriptionById, 
        actualizarFrecuenciaPorPlazas,
        bulkUpdateTariffs,
        cotizarSuscripcion,
        recalcularDesdePlazas,
        type BulkUpdateTariffsRequest } from '../api';

/**
 * Recalcular importes desde las plazas (todo el centro o un cliente). Con
 * `dryRun` solo devuelve qué cambiaría; los toasts los pone la pantalla.
 */
export const useRecalcularDesdePlazasMutation = () => useMutation({
    mutationFn: recalcularDesdePlazas,
    onSuccess: (_data, variables) => {
        if (variables.dryRun === false) {
            queryClient.invalidateQueries({ queryKey: ['client'] });
            invalidarListasDeCobro();
            queryClient.invalidateQueries({ queryKey: ['cotizacion'] });
        }
    },
    onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Error al recalcular los importes');
    },
});

/** Vista previa del importe de una suscripción con sus plazas (y una tarifa distinta, si se pasa). */
export const usePrecioDesdePlazasQuery = (params: { suscripcionId?: string; tarifasAsignadas?: string[]; enabled?: boolean }) => {
    const tenantCode = useTenantCode();
    const { suscripcionId, tarifasAsignadas, enabled = true } = params;
    return useQuery({
        queryKey: ['cotizacion', 'suscripcion', tenantCode, suscripcionId ?? null, tarifasAsignadas ?? null],
        queryFn: () => recalcularDesdePlazas({ dryRun: true, suscripcionId, tarifasAsignadas }),
        enabled: enabled && !!suscripcionId,
        staleTime: 0,
    });
};
import { Suscripcion as Subscription } from "../../types";
import { useTenantCode } from './useTenantCode';
import { invalidarListasDeCobro } from './invalidaciones';

/**
 * Precio de unas plazas (ver pricingService en el back). Lo calcula el servidor:
 * las tarifas solo las puede leer un admin y la inscripción la hace también un
 * operador. `faltaElegir` dice qué niveles necesitan que se elija tarifa.
 */
export const useCotizacionQuery = (params: {
    plazas: Array<{ nivel_id: string }>;
    tarifasAsignadas?: string[];
    suscripcionId?: string;
    enabled?: boolean;
}) => {
    const tenantCode = useTenantCode();
    const { plazas, tarifasAsignadas, suscripcionId, enabled = true } = params;
    return useQuery({
        queryKey: ['cotizacion', tenantCode, plazas.map((p) => p.nivel_id), tarifasAsignadas ?? null, suscripcionId ?? null],
        queryFn: () => cotizarSuscripcion({ plazas, tarifasAsignadas, suscripcionId }),
        enabled: enabled && plazas.length > 0,
        staleTime: 0,
        // Mientras se recalcula con otra tarifa, se sigue enseñando el resultado
        // anterior: si no, el selector desaparece y vuelve a cada cambio.
        placeholderData: (anterior) => anterior,
    });
};

interface UpdateSubscriptionParams {
    id: string;
    data: Partial<Subscription>;
}

// Mutación para crear suscripciones
export const useCreateSubscriptionMutation = () => useMutation({
    mutationFn: createSubscription,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
        toast.success('Suscripción creada exitosamente');
    },
    onError: () => {
        toast.error('Error al crear la suscripción');
    },
});

// Mutación para actualizar suscripciones
export const useUpdateSubscriptionMutation = () => useMutation({
    mutationFn: ({ id, data }: UpdateSubscriptionParams) => updateSubscription(id, data),
    onSuccess: () => {
        // Desde la ficha no se refrescaban SEPA ni Otros pagos.
        invalidarListasDeCobro();
        toast.success('Suscripción actualizada exitosamente');
    },
    onError: () => {
        toast.error('Error al actualizar la suscripción');
    },
});

// Mutación para eliminar suscripciones
export const useDeleteSubscriptionMutation = () => useMutation({
    mutationFn: deleteSubscription,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
        toast.success('Suscripción eliminada exitosamente');
    },
    onError: () => {
        toast.error('Error al eliminar la suscripción');
    },
});


//Mutación para actualizar beneficiario
export const useUpdateBeneficiarioMutation = () => useMutation({
    mutationFn: ({ id, beneficiarioId }: { id: string, beneficiarioId: string }) => updateBeneficiario(id, beneficiarioId),
    onSuccess: () => {
        // El pagador pasa a tener beneficiario: cambia lo que cobra SEPA.
        invalidarListasDeCobro();
        toast.success('Beneficiario actualizado exitosamente');
    },
    onError: () => {
        toast.error('Error al actualizar el beneficiario');
    },
});


export const useSubscriptionByIdQuery = (subscriptionId?: string, options = {}) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['subscription', subscriptionId, tenantCode],
        queryFn: () => fetchSubscriptionById(subscriptionId!),
        enabled: !!subscriptionId,
        retry: 1,
        retryDelay: 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
        staleTime: 60000,
        gcTime: 300000,
        ...options
    });
};


/**
 * Hook para actualizar automáticamente la frecuencia y tarifa de una suscripción
 * basándose en el número de plazas activas que tiene un cliente.
 * 
 * Este hook llama al endpoint del backend que:
 * 1. Cuenta las plazas activas del cliente
 * 2. Determina la frecuencia (1X, 2X o 3X)
 * 3. Obtiene la tarifa correspondiente de las configuraciones
 * 4. Actualiza la suscripción
 */
export const useActualizarFrecuenciaSuscripcionMutation = () => {
    return useMutation({
        mutationFn: actualizarFrecuenciaPorPlazas,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['client'] });
            
            // Mostrar mensaje informativo sobre la actualización
            if (data && data.frecuencia) {
                toast.success(`Suscripción actualizada a ${data.frecuencia} (${data.tarifa}€)`);
            } else {
                toast.success('Suscripción actualizada correctamente');
            }
        },
        onError: (error: any) => {
            const mensaje = error?.response?.data?.message || 'Error al actualizar la suscripción';
            toast.error(`Error: ${mensaje}`);
        }
    });
};

/**
 * Hook para actualizar tarifas de forma masiva en múltiples suscripciones.
 * 
 * Características:
 * - Permite actualizar todas las suscripciones activas en una sola operación
 * - Modo DryRun para previsualizar cambios sin ejecutarlos
 * - Transacción atómica: se aplican todos los cambios o ninguno
 * - Invalidación automática de caché después de ejecutar
 * 
 * @example
 * const mutation = useBulkUpdateTariffsMutation();
 * 
 * // Previsualizar cambios
 * mutation.mutate({
 *   nuevasTarifas: { "1XSemana": 75, "2XSemana": 125, "3XSemana": 170 },
 *   dryRun: true
 * });
 * 
 * // Ejecutar actualización
 * mutation.mutate({
 *   nuevasTarifas: { "1XSemana": 75, "2XSemana": 125, "3XSemana": 170 },
 *   updatedBy: "admin@example.com"
 * });
 */
export const useBulkUpdateTariffsMutation = () => {
    return useMutation({
        mutationFn: bulkUpdateTariffs,
        onSuccess: (data, variables) => {
            // Solo invalidar caché si NO es DryRun
            if (!variables.dryRun) {
                invalidarListasDeCobro();
                queryClient.invalidateQueries({ queryKey: ['configurations'] });
                
                toast.success(`${data.actualizadas} suscripciones actualizadas correctamente`);
            }
        },
        onError: (error: any) => {
            const mensaje = error?.response?.data?.message || 'Error al actualizar las tarifas';
            toast.error(`Error: ${mensaje}`);
        }
    });
};